import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef, useState } from "react";
import { KnowledgeCaptureContext } from "../../context/knowledge-capture-context";
import { KnowledgeCaptureModal } from "./knowledge-capture-modal";

const AUTO_DISMISS_MS = 8000;

export const KnowledgeCaptureToast = memo(function KnowledgeCaptureToast() {
  const { isOpen, conversationId, preview, hideKnowledgeCapture } =
    useRequiredContext(KnowledgeCaptureContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasInteractedRef = useRef(false);
  const prevIsOpenRef = useRef(false);

  // Reset interaction state when toast opens (transition from closed → open)
  // This ensures auto-dismiss works for subsequent notifications after the first one is dismissed
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      hasInteractedRef.current = false;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Auto-dismiss after 8 seconds, but only on first display before any interaction
  useEffect(() => {
    if (!isOpen || hasInteractedRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!hasInteractedRef.current) {
        hideKnowledgeCapture();
      }
    }, AUTO_DISMISS_MS);

    return () => clearTimeout(timeoutId);
  }, [isOpen, hideKnowledgeCapture]);

  const handleOpenModal = useHandler(() => {
    hasInteractedRef.current = true;
    setIsModalOpen(true);
  });

  const handleCloseModal = useHandler(() => {
    setIsModalOpen(false);
  });

  const handleDismissToast = useHandler(() => {
    hideKnowledgeCapture();
  });

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        style={{
          position: "fixed",
          bottom: "var(--redo-spacing-lg)",
          right: "var(--redo-spacing-lg)",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            backgroundColor: "var(--redo-colors-background-bg-primary)",
            border: "none",
            borderRadius: "var(--border-radius-xl)",
            boxShadow:
              "0px 8px 16px -4px rgba(16, 24, 40, 0.1), 0px 20px 24px -4px rgba(16, 24, 40, 0.1)",
            minWidth: "340px",
            maxWidth: "420px",
            overflow: "hidden",
          }}
        >
          <Flex dir="column" gap="none" w="full">
            <Flex
              align="center"
              justify="space-between"
              style={{
                background:
                  "linear-gradient(135deg, var(--redo-colors-orange-50) 0%, var(--redo-colors-orange-50) 100%)",
                padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
              }}
              w="full"
            >
              <Flex align="center" gap="sm">
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "var(--border-radius-full)",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AiStarIcon
                    style={{
                      width: "20px",
                      height: "20px",
                      color: "var(--redo-colors-orange-dark-500)",
                    }}
                  />
                </div>
                <Flex dir="column" gap="none">
                  <Text fontSize="sm" fontWeight="medium" textColor="primary">
                    Improve your AI's responses
                  </Text>
                  {preview && (
                    <Text fontSize="xs" textColor="secondary">
                      {preview}
                    </Text>
                  )}
                </Flex>
              </Flex>
              <Flex align="center" gap="xs">
                <RedoButton
                  hierarchy="secondary"
                  onClick={handleOpenModal}
                  size="xs"
                  text="Add knowledge"
                />
                <RedoButton
                  aria-label="Dismiss"
                  hierarchy="tertiary"
                  IconLeading={XCloseIcon}
                  onClick={handleDismissToast}
                  size="xs"
                />
              </Flex>
            </Flex>
          </Flex>
        </div>
      </div>
      {isModalOpen && (
        <KnowledgeCaptureModal
          isOpen
          key={conversationId}
          onCloseModal={handleCloseModal}
        />
      )}
    </>
  );
});
